import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { businessInfo, serviceOptions } from '../../data/mock';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (mock - would connect to backend)
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    console.log('Form submitted:', formData);
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after delay
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', phone: '', email: '', service: '', message: '' });
    }, 5000);
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#050505]">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00D4FF]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase mb-4 block">
            Contact Us
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Get Your Free Security Assessment
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Ready to secure your property, event, or business? Contact us today for a
            free consultation with our security experts.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8">Get in Touch</h3>
            
            <div className="space-y-6 mb-10">
              {/* Phone */}
              <a
                href={`tel:${businessInfo.phone}`}
                className="group flex items-start gap-4 p-6 bg-gradient-to-r from-white/[0.05] to-transparent border border-white/10 hover:border-[#00D4FF]/50 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 group-hover:bg-[#00D4FF] transition-all duration-300">
                  <Phone className="w-5 h-5 text-[#00D4FF] group-hover:text-black transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Call Us 24/7</p>
                  <p className="text-xl font-bold text-white group-hover:text-[#00D4FF] transition-colors">
                    {businessInfo.phone}
                  </p>
                </div>
              </a>

              {/* Email */}
              <a
                href={`mailto:${businessInfo.email}`}
                className="group flex items-start gap-4 p-6 bg-gradient-to-r from-white/[0.05] to-transparent border border-white/10 hover:border-[#00D4FF]/50 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 group-hover:bg-[#00D4FF] transition-all duration-300">
                  <Mail className="w-5 h-5 text-[#00D4FF] group-hover:text-black transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email Us</p>
                  <p className="text-lg font-semibold text-white group-hover:text-[#00D4FF] transition-colors">
                    {businessInfo.email}
                  </p>
                </div>
              </a>

              {/* Address */}
              <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-white/[0.05] to-transparent border border-white/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10">
                  <MapPin className="w-5 h-5 text-[#00D4FF]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Headquarters</p>
                  <p className="text-white">{businessInfo.address.street}</p>
                  <p className="text-white">
                    {businessInfo.address.city}, {businessInfo.address.state} {businessInfo.address.zip}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-white/[0.05] to-transparent border border-white/10 p-8 lg:p-10">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00D4FF] to-transparent" />
            
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 flex items-center justify-center bg-[#00D4FF]/10 mb-6">
                  <CheckCircle2 className="w-10 h-10 text-[#00D4FF]" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">
                  Message Sent Successfully!
                </h4>
                <p className="text-gray-400">
                  Thank you for contacting us. Our team will reach out to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h4 className="text-xl font-bold text-white mb-6">
                  Request a Free Assessment
                </h4>

                {/* Name */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Full Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-[#00D4FF] h-12"
                    placeholder="John Smith"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Phone Number *</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-[#00D4FF] h-12"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-[#00D4FF] h-12"
                    placeholder="john@company.com"
                  />
                </div>

                {/* Service Needed */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Service Needed *</label>
                  <Select onValueChange={handleServiceChange} value={formData.service}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white h-12 focus:border-[#00D4FF]">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                      {serviceOptions.map((option) => (
                        <SelectItem
                          key={option}
                          value={option}
                          className="text-white hover:bg-[#00D4FF]/10 focus:bg-[#00D4FF]/10 cursor-pointer"
                        >
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Additional Details</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-[#00D4FF] resize-none"
                    placeholder="Tell us about your security needs..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-3 bg-[#00D4FF] text-black px-8 py-4 font-semibold text-lg hover:shadow-[0_0_40px_rgba(0,212,255,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Request
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
